import React, { useState } from "react";
import { Button, Card, Col, Stack } from "react-bootstrap";
import Input from "./Input";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheck, faClose, faEdit, faGripLines, faTrash } from "@fortawesome/free-solid-svg-icons";
import IconButton from "./IconButton";

interface CardState {
  items: any[];
  onEditFinal: any;
  onDeleteFinal: any;
  updateOrder: any;
  customEditLevel?: any;
  customEditId?: string;
  setCustomEditId?: any;
  setErrMessage: any;
}

const ReorderEditDeleteCards = ({
  items,
  onEditFinal,
  onDeleteFinal,
  updateOrder,
  customEditLevel = null,
  customEditId = "",
  setCustomEditId = null,
  setErrMessage,
}: CardState) => {
  const [editId, setEditId] = useState<string>();
  const [deleteId, setDeleteId] = useState<string>();
  const [currentItemName, setCurrentItemName] = useState<string>();
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dropTargetIndex, setDropTargetIndex] = useState<number | null>(null);
  const [draggedProgramId, setDraggedProgramId] = useState<number | null>(null);

  const onClickEdit = (itemId: string) => {
    const itemName = items.find((i: any) => i.id === itemId).name;
    setCurrentItemName(itemName);
    if (setCustomEditId) {
      setCustomEditId(itemId);
    } else {
      setEditId(itemId);
    }
    setDeleteId("");
  };

  const onClickEditFinal = (itemId: string) => {
    if (!currentItemName || currentItemName.trim() === "") {
      setErrMessage("Please enter a value.");
      return;
    }

    setEditId("");
    onEditFinal(itemId, currentItemName);
    setCurrentItemName("");
  };

  const onClickEditCancel = () => {
    setEditId("");
    setCurrentItemName("");
  };

  const onClickDelete = (itemId: string) => {
    const item = items.find((i: any) => i.id === itemId);
    const itemName = item?.name ? item?.name : item?.question;
    setCurrentItemName(itemName);
    setEditId("");
    setDeleteId(itemId);
  };

  const onClickDeleteFinal = (itemId: string) => {
    setDeleteId("");
    onDeleteFinal(itemId, currentItemName);
    setCurrentItemName("");
  };

  const onClickDeleteCancel = () => {
    setDeleteId("");
    setCurrentItemName("");
  };

  const updateItem = (e: any) => {
    setCurrentItemName(e.target.value);
  };

  const handleDrop = (dropIndex: number) => {
    if (draggedIndex === null || draggedIndex === dropIndex) return;

    const reordered = [...items];
    const [moved] = reordered.splice(draggedIndex, 1);
    reordered.splice(dropIndex, 0, moved);
    updateOrder(reordered);
    setDraggedIndex(null);
  };

  const renderDeleteLevel = (levelId: any) => {
    return (
      <Card className="mb-2">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} gap={2}>
              <Card.Subtitle>Remove {currentItemName}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={() => onClickDeleteFinal(levelId)}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={() => onClickDeleteCancel()}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderEditLevel = (levelId: any) => {
    return (
      <Card className="mb-2">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Col>
              <Input label="Name" type="text" value={currentItemName} hasFocus={true} onUpdate={(e: any) => updateItem(e)} />
            </Col>
            <IconButton onClickButton={() => onClickEditFinal(levelId)} tooltipText={"Save"} icon={faCheck} />
            <div className="vr" />
            <IconButton onClickButton={() => onClickEditCancel()} tooltipText={"Cancel"} icon={faClose} />
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  return (
    <>
      {items?.map((item: any, index: number) =>
        deleteId === item.id ? (
          <div key={"render-delete-" + item.id}>{renderDeleteLevel(item.id)}</div>
        ) : customEditId === item.id ? (
          <div key={"render-edit-" + item.id}>{customEditLevel(item.id)}</div>
        ) : editId === item.id ? (
          <div key={"render-edit-" + item.id}>{renderEditLevel(item.id)}</div>
        ) : (
          <div
            key={item.id}
            draggable
            onDragStart={(e) => {
              setDraggedIndex(index);
              setDraggedProgramId(item.id);
              e.currentTarget.classList.add("dragging");
            }}
            onDragOver={(e) => {
              e.preventDefault();
              if (draggedProgramId === item.id) {
                setDropTargetIndex(null);
              } else {
                setDropTargetIndex(index);
              }
            }}
            onDragLeave={() => setDropTargetIndex(null)}
            onDrop={() => {
              if (draggedProgramId !== item.id) {
                handleDrop(index);
              }
              setDropTargetIndex(null);
              setDraggedProgramId(null);
            }}
            onDragEnd={() => {
              document.querySelectorAll(".dragging").forEach((el) => el.classList.remove("dragging"));
              setDraggedIndex(null);
              setDraggedProgramId(null);
              setDropTargetIndex(null);
            }}
            className={dropTargetIndex === index ? "drop-target" : ""}
          >
            <Card className="mb-2">
              <Card.Body className="px-3 py-2">
                <Stack direction="horizontal" gap={3}>
                  <Col style={{ width: "20.5rem", overflow: "hidden", textOverflow: "ellipsis" }}>
                    <span style={{ fontSize: "1rem", cursor: "grab" }}>
                      <FontAwesomeIcon icon={faGripLines} className={"me-2"} />
                    </span>
                    {item.name ? item.name : item.question}
                  </Col>
                  <IconButton onClickButton={() => onClickEdit(item.id)} tooltipText={"Edit"} icon={faEdit} />
                  <div className="vr" />
                  <IconButton onClickButton={() => onClickDelete(item.id)} tooltipText={"Delete"} icon={faTrash} />
                </Stack>
              </Card.Body>
            </Card>
          </div>
        )
      )}
    </>
  );
};

export default ReorderEditDeleteCards;
